import paper from 'paper';
import { Path, Tool, Rectangle, Point } from 'paper';
import store from '../store';
import PathStyle from '@/services/PathStyle';
import PainterService from '@/services/PainterService';
import ToolsUtils from '@/services/ToolsUtils';
import PathUtils from '@/services/PathUtils';

/* eslint-disable no-undef */
const local = {
    itemIds: null,
    shape: null,
    scalingCenterPoint: null,
    handle: null,
    transforming: null,
    moving: false,
    resizing: false,
    selecting: false,
    displayPosition: true,
    customHandle: null,
    spaceKey:false,
    debounce: null,
    mousePosition: [],
    canPaste: true,
    magneticPositionTargetsX: [],
    magneticPositionTargetsY: [],
    magneticSizeTargets: [],
    magneticMatch: {x: null, y: null, width: null, height: null},

    get selectedShapes() {
        return store.getters.selectedShapeIds.map(id => store.getters.shapes.find(s => s.id == id));
    },

    get selectionRectangle() {
        if (paper.project == null) return null;
        return paper.project.getItem({ name: "selection" });
    }
};

function hitOptions() {
    return {
        segments: true,
        stroke: true,
        curves: true,
        fill: true,
        guide: false,
        tolerance: store.getters.mobile ? 15 / store.getters.zoom : 8 / store.getters.zoom
    };
}

function getBounds(items) {
    let minTop = 9999999,
        maxBottom = -99999,
        minLeft = 999999,
        maxRight = -99999;

    items.forEach(item => {
        let bounds = item.bounds;
        minTop = bounds.top < minTop ? bounds.top : minTop;
        maxBottom = bounds.bottom > maxBottom ? bounds.bottom : maxBottom;
        minLeft = bounds.left < minLeft ? bounds.left : minLeft;
        maxRight = bounds.right > maxRight ? bounds.right : maxRight;
    })

    return new Rectangle(new Point(minLeft, minTop), new Point(maxRight, maxBottom) );
}

function initSelectionRectangle() {
    PainterService.removePosition();
    let selectionRectangle = local.selectionRectangle;

    if (selectionRectangle != null) {
        selectionRectangle.remove();
    }

    if (local.customHandle != null) {
        local.customHandle.remove();
        local.customHandle = null;
    }

    let items = selectedItems();

    if (items.length == 0) return;

    let position;
    if (store.getters.selectedShape) {

        let shape = store.getters.selectedShape;
        let item = shape.item();
        position = shape.referencePoint();

        if (shape.accepts('rotate')) {
            let copy = item.copyTo(paper.project.layers.temp);
            copy.rotate(-shape.rotation, position);

            let b = copy.bounds.clone();
            copy.remove();

            selectionRectangle = new Path.Rectangle(b);

            selectionRectangle.insert(1, new Point(b.left, b.center.y));
            selectionRectangle.insert(3, new Point(b.center.x, b.top));
            selectionRectangle.insert(4, new Point(b.center.x, b.top - (30 / store.getters.zoom)));
            selectionRectangle.insert(5, new Point(b.center.x, b.top));
            selectionRectangle.insert(7, new Point(b.right, b.center.y));
            selectionRectangle.insert(9, new Point(b.center.x, b.bottom));

            selectionRectangle.rotate(shape.rotation, position);
        } else {
            let copy = item.copyTo(paper.project.layers.temp);

            let b = copy.bounds.clone();
            copy.remove();

            selectionRectangle = new Path.Rectangle(b);

            if (shape.accepts('resize', 'resize-radius'))
            {
                selectionRectangle.insert(1, new Point(b.left, b.center.y));
                selectionRectangle.insert(3, new Point(b.center.x, b.top));
                selectionRectangle.insert(5, new Point(b.right, b.center.y));
                selectionRectangle.insert(7, new Point(b.center.x, b.bottom));
            }
        }

        if (shape.accepts('triangle')) {
            let point = shape.point
                .add(new Point(shape.size.width * shape.heightPos, (-10 / store.getters.zoom)))
                .rotate(shape.rotation, shape.center);

            local.customHandle = new Path.Circle(point, (6 / store.getters.zoom));
            paper.project.layers.selecter.addChild(local.customHandle);
            local.customHandle.name = "triangle";
            PathStyle.customHandle(local.customHandle);
        }
    }
    else {
        let b = getBounds(items).clone();
        position = b.center;
        selectionRectangle = new Path.Rectangle(b);
        
        if (local.selectedShapes.every(s => s.accepts("rotate"))) {
            selectionRectangle.insert(1, new Point(b.left, b.center.y));
            selectionRectangle.insert(3, new Point(b.center.x, b.top));
            selectionRectangle.insert(4, new Point(b.center.x, b.top - (30 / store.getters.zoom)));
            selectionRectangle.insert(5, new Point(b.center.x, b.top));
            selectionRectangle.insert(7, new Point(b.right, b.center.y));
            selectionRectangle.insert(9, new Point(b.center.x, b.bottom));
        }else if (local.selectedShapes.every(s => s.accepts("resize", 'resize-radius'))) {
            selectionRectangle.insert(1, new Point(b.left, b.center.y));
            selectionRectangle.insert(3, new Point(b.center.x, b.top));
            selectionRectangle.insert(5, new Point(b.right, b.center.y));
            selectionRectangle.insert(7, new Point(b.center.x, b.bottom));
        }
    }

    paper.project.layers.selecter.addChild(selectionRectangle);

    selectionRectangle.name = "selection";

    PathStyle.selectionRectangle(selectionRectangle);

    selectionRectangle.selected = true;
    if (items.length > 1) {
        items.forEach(item => item.selected = true);
    } else {
        items.forEach(item => item.selected = false);
    }

    if (local.displayPosition) PainterService.displayPosition(position);
    else PainterService.removePosition();
}

function initializeSelectingRectangle(event) {
    let selectingRectangle = new Path.Rectangle(
        new Rectangle(event.downPoint, event.point)
    );

    paper.project.layers.view.addChild(selectingRectangle);

    selectingRectangle.name = "selecting rectangle";
    selectingRectangle.removeOnDrag();
    selectingRectangle.removeOnUp();

    PathStyle.selectingRectangle(selectingRectangle);
}

function selectedItemsId() {
    return local.selectedShapes.map(shape => shape.pathId);
}

function selectedItems() {
    let ret = local.selectedShapes.map(s => {
        let item = s.item();
        if (item == null) throw "Items missing for shape " + s.id;
        return item;
    });

    return ret;
}

const uniqueRotatableHandles = ['corner-bottom-left', 'width-left', 'corner-top-left', 'height-top', 'rotate', 'height-top', 'corner-top-right', 'width-right', 'corner-bottom-right', 'height-bottom'];
const uniqueNonRotatableHandles = ['corner-bottom-left', 'width-left', 'corner-top-left', 'height-top', 'corner-top-right', 'width-right', 'corner-bottom-right', 'height-bottom'];
const multipleHandles = ['corner-bottom-left', 'corner-top-left', 'corner-top-right', 'corner-bottom-right'];

function detectHandle(hitResult) {
    if (store.getters.selectedShape || local.selectedShapes) {
        if (local.selectedShapes.every(s => s.accepts("rotate"))) return uniqueRotatableHandles[hitResult.segment.index];
        else return uniqueNonRotatableHandles[hitResult.segment.index];
    } else return multipleHandles[hitResult.segment.index];
}

const uniqueRotatableScaleCenterPoints = ['corner-top-right', 'width-right', 'corner-bottom-right', 'height-bottom', 'rotate', 'height-bottom', 'corner-bottom-left', 'width-left', 'corner-top-left', 'height-top'];
const uniqueNonRotatableScaleCenterPoints = ['corner-top-right', 'width-right', 'corner-bottom-right', 'height-bottom', 'corner-bottom-left', 'width-left', 'corner-top-left', 'height-top'];
const multipleScaleCenterPoints = ['corner-top-right', 'corner-bottom-right','corner-bottom-left', 'corner-top-left'];

function scaleCenterPoint(handle) {
    let centerPointIndex = null;
    if (store.getters.selectedShape || local.selectedShapes) {
        if (local.selectedShapes.every(s => s.accepts("rotate"))) centerPointIndex = uniqueRotatableScaleCenterPoints.indexOf(handle);
        else centerPointIndex = uniqueNonRotatableScaleCenterPoints.indexOf(handle);
    } else centerPointIndex = multipleScaleCenterPoints.indexOf(handle);
    return local.selectionRectangle.segments[centerPointIndex].point;

}

function startTransforming() {
    local.transforming = true;

    let items = selectedItems();
    items.forEach(item => PathStyle.transform(item));
    PathStyle.selectionRectangleInUse(local.selectionRectangle);

    store.dispatch('preventRepaint');
    store.dispatch('preventCommit');
}

function stopTransforming() {
    local.transforming = false;
    local.moving = false;
    local.handle = null;
    local.resizing = false;
    local.magneticMatch = {x: null, y: null, width: null, height: null};
    let items = selectedItems();
    items.forEach(item => PathStyle.transparent(item));
    PathStyle.selectionRectangle(local.selectionRectangle);

    store.dispatch('allowRepaint');
    store.dispatch('allowCommit');
}

function isClick(event) {
    return event.downPoint == null || event.downPoint.isClose(event.point, hitOptions().tolerance);
}

function onMouseDown(event) {

    if (event.event.button > 0 || local.spaceKey) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
    if(store.getters.optimisationMode){
        return false;
    }

    local.handle = null;

    let selectionRectangle = local.selectionRectangle;

    let selecterHitResult = selectionRectangle ? selectionRectangle.hitTest(event.point, hitOptions()) : null;
    let customHandleHitResult = local.customHandle ? local.customHandle.hitTest(event.point, hitOptions()) : null;
    let hitShape = getHitShape(event);

    if (customHandleHitResult) {
        local.handle = local.customHandle.name;
        changeCursor('grabbing');
        startTransforming();
    }
    else if (selecterHitResult) {
        local.selecting = null;
        let shape = store.getters.selectedShape;
        if (selecterHitResult.type === "segment" && local.selectedShapes.length > 0 && local.selectedShapes.every(s => s.accepts('resize', 'resize-radius', 'rotate'))) {
            local.handle = detectHandle(selecterHitResult);

            if (local.handle == null) {
                console.error("No handle detected");
                return;
            }

            if (local.handle == 'rotate') {
                let center,rotation;
                if(local.selectedShapes.length > 1){
                    center = getBounds(selectedItems()).center;
                    rotation = 0;
                }else{
                    center = shape.center;
                    rotation = shape.rotation;
                }
                changeCursor('grabbing');
                drawCenter(center);
                mousetip(event, '∠ : ' + Math.round(rotation) + ' °');

            } else {
                local.scalingCenterPoint = scaleCenterPoint(local.handle);
                if(local.selectedShapes.length > 1){
                    let items = selectedItems();
                    let bounds = getBounds(items);
                    changeCursor(resizeCursor(local.handle, 0));
                    mousetip(event, 'L : ' + Math.round(bounds.size.width) + ' mm\nH : ' + Math.round(bounds.size.height) + ' mm');    
                }else{
                    changeCursor(resizeCursor(local.handle, shape.rotation));
                    mousetip(event, 'L : ' + Math.round(store.getters.selectedShape.bounds.size.width) + ' mm\nH : ' + Math.round(store.getters.selectedShape.bounds.size.height) + ' mm');
                }
            }
            startTransforming();
        }
        else if(local.selectedShapes.length > 1){
            select(local.selectedShapes);
        }
        else {
            if (!local.selectedShapes.includes(hitShape)) {
                select([hitShape]);
            }
        }
    } else {
        if (hitShape) {
            select([hitShape]);
            local.selecting = null;
        } else {
            deselect();
            local.selecting = true;
            store.dispatch('setDropped', null);

        }
    }
}

async function onMouseUp(event) {
    changeCursor('default');
    if(event.event.button > 0 || local.spaceKey) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
    if(store.getters.optimisationMode){
        return false;
    }

    if (local.transforming) {
        stopTransforming();

        local.displayPosition = store.getters.selectedShape != null;
        initSelectionRectangle();
        return;
    }

    if (isClick(event)) {
        let shape = getHitShape(event);
            if (shape == null) {
            if (local.selectedShapes && local.selectedShapes.length > 0) {
                deselect();
            }
        } else {
            if (!local.selectedShapes.includes(shape)) select([shape]);
        }

        local.selected = shape;

        return;
    }

    paper.project.getItems({selected: true}).forEach(item => item.selected = false);

    let selectedItems = getItemsIn(new Rectangle(event.downPoint, event.point));
    selectedItems.forEach(item => item.selected = true);
    local.selecting = null;

    select(selectedItems.map(i => store.getters.shapes.find(shape => shape.pathId == i.id)));
}


function onMouseMove(event) {
    local.mousePosition = event.point;
    if (store.getters.multitouch) {
        if (local.selecting) {
            local.selecting = null;
        }
        if (local.transforming) {
            stopTransforming();
        }
        return;
    }
    if(store.getters.optimisationMode){
        return false;
    }

    let selectedIds = selectedItemsId();
    paper.project.getItems({selected: true})
        .forEach(item => item.selected = selectedIds.length > 1 && selectedIds.includes(item.id));

    if (event.item && event.item.layer.name == 'selectable' && !selectedItemsId().includes(event.item.id)){
        event.item.selected = true;
    }

    let selectionRectangle = local.selectionRectangle;
    if (selectionRectangle != null) {
        selectionRectangle.selected = true;
    }

    let selecterHitResult = selectionRectangle ? selectionRectangle.hitTest(event.point, hitOptions()) : null;

    let cursor = 'default';
    if (selecterHitResult) {
        local.selecting = null;
        if (store.getters.selectedShapeIds.length > 1 && selecterHitResult.type === "segment") {
            local.handle = detectHandle(selecterHitResult);
            cursor = local.handle == 'rotate'
                ? 'grabbing'
                : resizeCursor(local.handle, 0);
            
        }else if (selecterHitResult.type === "segment" && store.getters.selectedShape.accepts('resize', 'resize-radius', 'rotate')) {
            local.handle = detectHandle(selecterHitResult);
            cursor = local.handle == 'rotate'
                ? 'grabbing'
                : resizeCursor(local.handle, store.getters.selectedShape.rotation);
        }
    }

    changeCursor(cursor);
}

async function onMouseDrag(event) {
    if((event.event.buttons == 2 || (event.event.buttons == 1 && local.spaceKey) || event.event.buttons == 4) && store.getters.shapes.length){
        var newCenter = event.downPoint.subtract(event.point);
        newCenter = newCenter.add(paper.view.center);
        paper.view.center = newCenter;
        changeCursor("move");
    }
    if(store.getters.optimisationMode){
        return false;
    }

    if(event.event.buttons == 1 && !local.spaceKey || event.event.buttons == undefined && !local.spaceKey){
        if (store.getters.multitouch) {
            if (local.selecting) {
                local.selecting = null;
            }
            if (local.transforming) {
                stopTransforming();
            }
            return;
        }
        if (local.selecting) {
            initializeSelectingRectangle(event);

            paper.project.getItems({ selected: true })
                .forEach(item => item.selected = false);

            getItemsIn(new Rectangle(event.downPoint, event.point)).forEach(item => item.selected = true);

            return;
        }
        let selectionRectangle = local.selectionRectangle;

        if (local.handle == 'triangle') {
            let heightPos = getHeightOffset(store.getters.selectedShape, event.point);

            store.dispatch('setHeightPos', heightPos);
            mousetip(event, ' Sommet : ' + Math.round(heightPos) + ' mm');
            return;
        }
        if (local.handle == 'rotate') {
            let center;
            if(local.selectedShapes.length > 1){
                center = getBounds(selectedItems()).center;
            }else{
                center = store.getters.selectedShape.center;
            }
            let rotation = event.point.subtract(center).angle + 90;

            store.dispatch('setRotation', rotation);
            mousetip(event, '∠ : ' + Math.round(rotation) + ' °');
            return;
        }

        // resize mode
        if (local.handle != null && local.selectedShapes.every(s => !s.centerMarking)) {
            if (!local.resizing) {
                local.resized = false;
                local.resizing = true;
                local.magneticMatch = {x: null, y: null, width: null, height: null};
                buildMagneticPositionTargets();
                startTransforming();
            }
            if(local.selectedShapes.length > 1){
                let bounds = getBounds(selectedItems());
                    let oldSize = bounds.size;
                    let oldCenterBounds = bounds.center;
                    let { circleCenter, radius } = ToolsUtils.centerAndRadius(local.scalingCenterPoint, event.point, local.handle);
                    let { size, center } = getSizeAndCenter(event.point);
                    await store.dispatch('setRadius', {radius, center, size, oldSize, oldCenterBounds});
                    store.dispatch('setSize', {size, center, oldSize, oldCenterBounds});
                    mousetip(event, 'L : ' + Math.round(size.width) + ' mm\nH : ' + Math.round(size.height) + ' mm');
            }else{
                if (store.getters.selectedShape.accepts("resize-radius")) {
                    let { center, radius } = ToolsUtils.centerAndRadius(local.scalingCenterPoint, event.point, local.handle);
                    await store.dispatch('setRadius', {radius, center});
                    await store.dispatch('setShapeCenter', center);
                    mousetip(event, '⌀ : ' + (Math.round(store.getters.selectedShape.radius * 10) /10)*2 + ' mm');
                }
                else {
                    let realSize = getSizeAndCenter(event.point).size;
    
                    detectSizeMatch(realSize);
    
                    let magneticSizeDelta = new Size(0, 0);
                    if (local.magneticMatch.width) {
                        if (local.handle.startsWith('width') || local.handle.startsWith('corner')) {
                            magneticSizeDelta.width = local.magneticMatch.width.delta;
                        } else {
                            local.magneticMatch.width = null;
                        }
                    }
                    if (local.magneticMatch.height) {
                        if (local.handle.startsWith('height') || local.handle.startsWith('corner')) {
                            magneticSizeDelta.height = local.magneticMatch.height.delta;
                        } else {
                            local.magneticMatch.height = null;
                        }
                    }
    
                    let magneticMouseDelta = getMouseDelta(magneticSizeDelta);
                    let magneticMousePos = event.point.add(magneticMouseDelta)
    
                    let { size, center } = getSizeAndCenter(magneticMousePos);
                    store.dispatch('setSize', {size, center});
                    store.dispatch('setShapeCenter', center);
    
                    mousetip(event, 'L : ' + Math.round(store.getters.selectedShape.size.width) + ' mm\nH : ' + Math.round(store.getters.selectedShape.size.height) + ' mm');
    
                    drawMagnetic();
                }
            }
            this.initSelectionRectangle();
            return;
        }

        // move mode
        if (local.selectedShapes.length > 0 && local.selectedShapes.every(s => s.accepts('move')) && selectionRectangle !== null) {
            if (!local.moving) {
                local.moving = true;
                local.magneticMatch = { x: null, y: null };
                buildMagneticPositionTargets();
                startTransforming();
                changeCursor('grabbing');
            }
            let items = selectedItems();

            let revertMagneticDelta = new Point(
                local.magneticMatch.x == null ? 0 : -local.magneticMatch.x.delta,
                local.magneticMatch.y == null ? 0 : -local.magneticMatch.y.delta
            );

            let delta = event.delta.add(revertMagneticDelta);

            selectionRectangle.position = selectionRectangle.position.add(delta);
            items.forEach(item => item.position = item.position.add(delta));

            detectMagneticMatch(items);

            let magneticDelta = new Point(
                local.magneticMatch.x == null ? 0 : local.magneticMatch.x.delta,
                local.magneticMatch.y == null ? 0 : local.magneticMatch.y.delta
            );

            selectionRectangle.position = selectionRectangle.position.add(magneticDelta);
            items.forEach(item => item.position = item.position.add(magneticDelta));

            store.dispatch('moveShape', delta.add(magneticDelta));

            drawMagnetic();

            if (local.displayPosition){
                if(store.getters.selectedShape){
                    let position = store.getters.selectedShape.referencePoint();
                    PainterService.displayPosition(position);
                }else if(local.selectedShapes > 1){
                    let b = getBounds(selectedItems());
                    position = b.center;
                    PainterService.displayPosition(position);
                }
            }
            else PainterService.removePosition();
        }
    }
}

function getMouseDelta(sizeDelta){
    let isLeft = local.handle.indexOf('left') > -1,
        isTop = local.handle.indexOf('top') > -1,
        mouseDelta = new Point(
            isLeft ? -sizeDelta.width : sizeDelta.width,
            isTop ? -sizeDelta.height : sizeDelta.height
        );

    mouseDelta.rotate(store.getters.selectedShape.rotation);

    return mouseDelta;
}

function resizeCursor(handle, rotation) {
    let angle;

    if (handle.startsWith('width')) angle = 90;
    else if (handle.startsWith('height')) angle = 0;
    else if (handle == 'corner-bottom-left' || handle == 'corner-top-right') angle = 45;
    else if (handle == 'corner-top-left' || handle == 'corner-bottom-right') angle = 135;

    if (rotation) {
        angle += rotation;
    }

    if (angle < 0) angle += 180;
    if (angle > 180) angle -= 180;

    if (angle < 22 || angle >= 157) return 'ns-resize';
    if (angle >= 22 && angle < 67) return 'nesw-resize';
    if (angle >= 67 && angle < 112) return 'ew-resize';
    if (angle >= 112 && angle < 157) return 'nwse-resize';
}

function getItemsIn(rect) {
    return paper.project.layers.selectable.getItems().filter(item => item.bounds.center.isInside(rect) && item.name != "selecting rectangle");
}

function getSizeAndCenter(point) {
    let size,
        centerVector, 
        rot,
        bounds,
        shape;
    if(local.selectedShapes.length > 1){
        rot = 0;
    }else{
        shape = store.getters.selectedShape;
        rot = shape.rotation;
    }

    if (rot) {
        point = point.rotate(-rot, local.scalingCenterPoint);
    }

    let v = point.subtract(local.scalingCenterPoint),
        width = v.x,
        height = v.y;

    if (width > -1 && width < 0) width = -1;
    if (width > 0 && width < 1) width = 1;
    if (height > -1 && height < 0) height = -1;
    if (height > 0 && height < 1) height = 1;
    if(shape){
        bounds = shape.bounds;
    }else{
        bounds = getBounds(selectedItems());
    }
    if (local.handle.startsWith('width')) {
        size = {
            width: Math.abs(Math.round(width)),
            height: Math.round(bounds.size.height)
        };
        centerVector = new Point(width / 2, 0);
    }
    if (local.handle.startsWith('height')) {
        size = {
            width: Math.round(bounds.size.width),
            height: Math.abs(Math.round(height))
        };
        centerVector = new Point(0, height / 2);
    }
    if (local.handle.startsWith('corner')) {
        size = {
            width: Math.abs(Math.round(width)),
            height: Math.abs(Math.round(height))
        };
        centerVector = new Point(width / 2, height / 2);
    }

    let center = local.scalingCenterPoint.add(centerVector);

    if (rot) {
        center = center.rotate(rot, local.scalingCenterPoint);
    }
    
    return {size, center};
}

function getHeightOffset(shape, point) {
    let a = shape.center.subtract(new Point(shape.size.width / 2, shape.size.height / 2 + 10 / store.getters.zoom)),
        b = shape.center.subtract(new Point(-shape.size.width / 2, shape.size.height / 2 + 10 / store.getters.zoom)),
        base = new Path.Line(a, b);
    base.rotate(shape.rotation, shape.center);



    let proj = base.getNearestLocation(point);

    local.customHandle.remove();

    local.customHandle = new Path.Circle(proj.point, (6 / store.getters.zoom));
    paper.project.layers.selecter.addChild(local.customHandle);
    local.customHandle.name = "triangle";
    PathStyle.customHandle(local.customHandle);

    return proj.offset;
}

function changeCursor(cursor) {
    let canvas = document.getElementById('js-editor');
    if (canvas) canvas.style.cursor = cursor;
}

function mousetip(event, content) {
    let item = paper.project.getItem({name: 'mousetip'});

    if (!item) {
        item = new PointText(event.point);
        item.name = 'mousetip';
        item.style = {
            fontFamily: 'Open Sans',
            fontWeight: 'bold',
            fontSize: store.getters.mobile ? 20 / store.getters.zoom : 15 / store.getters.zoom,
            fillColor: '#59ABFF',
            justification: 'center',
            shadowColor: new Color(1, 1, 1),
            shadowBlur: 10 / store.getters.zoom,
            shadowOffset: new Point(0, 0)
        };
    }
    item.content = content;
    if (store.getters.mobile)
    {
        item.point = store.getters.center.add(new Point(0, -300 / store.getters.zoom));
    }
    else
    {
        item.point = event.point.add(
            new Point((20 / store.getters.zoom) + (item.bounds.width / 2), -20 / store.getters.zoom)
        );
    }
}

function buildMagneticPositionTargets() {

    local.magneticPositionTargetsX = [];
    local.magneticPositionTargetsY = [];
    local.magneticSizeTargets = [];

    local.magneticPositionTargetsX.push({
        point: store.getters.origin,
        position: store.getters.origin.x,
        type: 'point'
    });

    local.magneticPositionTargetsY.push({
        point: store.getters.origin,
        position: store.getters.origin.y,
        type: 'point'
    });

    store.getters.shapes
        .filter(s => !store.getters.selectedShapeIds.includes(s.id))
        .forEach(shape => {
            let bounds = shape.item().bounds;
            if (bounds) {
                local.magneticPositionTargetsX.push({
                    shape,
                    position: bounds.center.x,
                    type: 'centerx'
                });
                local.magneticPositionTargetsX.push({
                    shape,
                    position: bounds.left,
                    type: 'left'
                });
                local.magneticPositionTargetsX.push({
                    shape,
                    position: bounds.right,
                    type: 'right'
                });

                local.magneticPositionTargetsY.push({
                    shape,
                    position: bounds.center.y,
                    type: 'centery'
                });
                local.magneticPositionTargetsY.push({
                    shape,
                    position: bounds.top,
                    type: 'top'
                });
                local.magneticPositionTargetsY.push({
                    shape,
                    position: bounds.bottom,
                    type: 'bottom'
                });
            }
            let size = shape.size;
            if (size) {
                local.magneticSizeTargets.push({
                    shape,
                    value: shape.size.height,
                    type: 'height'
                });

                local.magneticSizeTargets.push({
                    shape,
                    value: shape.size.width,
                    type: 'width'
                });
            }
        }
    );

    let byPosition = (a, b) => a.position - b.position;

    local.magneticPositionTargetsX.sort(byPosition);
    local.magneticPositionTargetsY.sort(byPosition);
}

function detectMagneticMatch(items) {
    local.magneticMatch = { x: null, y: null };
    if (local.magneticPositionTargetsX.length == 0) return;
    items.forEach(item => {
        let centerx = getClosestMagneticPosition('centerx', item.bounds.center.x, local.magneticPositionTargetsX);
        let left = getClosestMagneticPosition('left', item.bounds.left, local.magneticPositionTargetsX);
        let right = getClosestMagneticPosition('right', item.bounds.right, local.magneticPositionTargetsX);

        let closestX = centerx.distance < left.distance ?
                            centerx.distance < right.distance ? centerx : right
                            : left.distance < right.distance ? left : right;
        closestX.item = item;

        if (local.magneticMatch.x == null || closestX < local.magneticMatch.x.distance) local.magneticMatch.x = closestX;

        let centery = getClosestMagneticPosition('centery', item.bounds.center.y, local.magneticPositionTargetsY);
        let top = getClosestMagneticPosition('top', item.bounds.top, local.magneticPositionTargetsY);
        let bottom = getClosestMagneticPosition('bottom', item.bounds.bottom, local.magneticPositionTargetsY);

        let closestY = centery.distance < top.distance ?
            centery.distance < bottom.distance ? centery : bottom
            : top.distance < bottom.distance ? top : bottom;
        closestY.item = item;

        if (local.magneticMatch.y == null || closestY < local.magneticMatch.y.distance) local.magneticMatch.y = closestY;
    });

    let epsilon = 8 / store.getters.zoom;

    if (local.magneticMatch.x.distance > epsilon) local.magneticMatch.x = null;
    if (local.magneticMatch.y.distance > epsilon) local.magneticMatch.y = null;
}

function getClosestMagneticPosition(type, position, positionTargets) {
    if (positionTargets.length == 0) return null;
    let i = 0;
    while (i < positionTargets.length && positionTargets[i].position < position) i++;
    if (i == 0) {
        let target = positionTargets[0];
        return {
            type,
            delta: target.position - position,
            distance: Math.abs(target.position - position),
            target
        }
    }

    if (i == positionTargets.length) {
        let target = positionTargets[i - 1];
        return {
            type,
            delta: target.position - position,
            distance: Math.abs(target.position - position),
            target
        }
    }

    let before = positionTargets[i-1],
        after = positionTargets[i],
        lbefore = position - before.position,
        lafter = after.position - position;
    if (lbefore < lafter) {
        return {
            type,
            delta: before.position - position,
            distance: lbefore,
            target: before
        };
    } else {
        return {
            type,
            delta: after.position - position,
            distance: lafter,
            target: after
        };
    }
}

function drawMagnetic() {
    let zoom = store.getters.zoom,
        paddingVector = new Point(16 / zoom, 0),
        arrowSize = 10 / zoom;

    let existing = paper.project.getItem({ name: 'magnetic' });
    if (existing) existing.remove();

    let group = new Group({ name: 'magnetic' });
    if (local.magneticMatch.x) {
        let from, to;
        let itemBounds = local.magneticMatch.x.item.bounds,
            target = local.magneticMatch.x.target;

        if (target.type == 'point') {
            from = local.magneticMatch.x.target.point;

            switch (local.magneticMatch.x.type) {
                case 'centerx':
                    to = new Point(
                        from.x,
                        itemBounds.center.y,
                    );

                    group.addChild(drawCenter(itemBounds.center));

                    break;
                case 'left':
                case 'right':
                    to = new Point(
                        from.x,
                        itemBounds.top,
                    );

                    break;
                default:
                    throw new Error('Unknown magneticMatch type in X ' + local.magneticMatch.x.type);
            }
        } else {
            let targetBounds = local.magneticMatch.x.target.shape.item().bounds;
            switch (local.magneticMatch.x.type) {
                case 'centerx':
                    from = new Point(
                        itemBounds.center.x,
                        Math.min(itemBounds.top, targetBounds.top)
                    );

                    group.addChild(drawCenter(itemBounds.center));

                    break;
                case 'left':
                    from = new Point(
                        itemBounds.left,
                        Math.min(itemBounds.top, targetBounds.top)
                    );

                    break;
                case 'right':
                    from = new Point(
                        itemBounds.right,
                        Math.min(itemBounds.top, targetBounds.top)
                    );

                    break;
                default:
                    throw new Error('Unknown magneticMatch type in X ' + local.magneticMatch.x.type);
            }

            switch (target.type) {
                case 'centerx':
                    to = new Point(
                        targetBounds.center.x,
                        Math.max(itemBounds.bottom, targetBounds.bottom)
                    );

                    group.addChild(drawCenter(targetBounds.center));
                    break;

                case 'left':
                    to = new Point(
                        targetBounds.left,
                        Math.max(itemBounds.bottom, targetBounds.bottom)
                    );
                    break;
                case 'right':
                    to = new Point(
                        targetBounds.right,
                        Math.max(itemBounds.bottom, targetBounds.bottom)
                    );
                    break;
                default:
                    throw new Error('Unknown magneticMatch type in X ' + local.magneticMatch.x.target.type);
            }
        }

        let line = new Path.Line(from, to);
        PathStyle.magneticLine(line);
        group.addChild(line);
    }


    if (local.magneticMatch.y) {
        let from, to;
        let itemBounds = local.magneticMatch.y.item.bounds,
            target = local.magneticMatch.y.target

        if (target.type == 'point') {
            from = local.magneticMatch.y.target.point;

            switch (local.magneticMatch.y.type) {
                case 'centery':
                    to = new Point(
                        itemBounds.center.x,
                        from.y,
                    );

                    group.addChild(drawCenter(itemBounds.center));

                    break;
                case 'top':
                case 'bottom':
                    to = new Point(
                        itemBounds.right,
                        from.y,
                    );

                    break;
                default:
                    throw new Error('Unknown magneticMatch type in X ' + local.magneticMatch.x.type);
            }
        } else {
            let targetBounds = target.shape.item().bounds;
            switch (local.magneticMatch.y.type) {
                case 'centery':
                    from = new Point(
                        Math.min(itemBounds.left, targetBounds.left),
                        itemBounds.center.y
                    );
                    group.addChild(drawCenter(itemBounds.center));

                    break;
                case 'top':
                    from = new Point(
                        Math.min(itemBounds.left, targetBounds.left),
                        itemBounds.top
                    );

                    break;
                case 'bottom':
                    from = new Point(
                        Math.min(itemBounds.left, targetBounds.left),
                        itemBounds.bottom
                    );
                    break;
                default:
                    throw new Error('Unknown magneticMatch type in Y ' + local.magneticMatch.y.type);
            }


            switch (target.type) {
                case 'centery':
                    to = new Point(
                        Math.max(itemBounds.right, targetBounds.right),
                        targetBounds.center.y
                    );

                    group.addChild(drawCenter(targetBounds.center));

                    break;
                case 'top':
                    to = new Point(
                        Math.max(itemBounds.right, targetBounds.right),
                        targetBounds.top
                    );
                    break;
                case 'bottom':
                    to = new Point(
                        Math.max(itemBounds.right, targetBounds.right),
                        targetBounds.bottom
                    );
                    break;
                default:
                    throw new Error('Unknown magneticMatch type in Y ' + local.magneticMatch.y.target.type);
            }
        }

        let line = new Path.Line(from, to);
        PathStyle.magneticLine(line);
        group.addChild(line);
    }

    if (local.magneticMatch.width) {
        let from, to, itemFrom, itemTo;
        let shape = store.getters.selectedShape,
            target = local.magneticMatch.width.target,
            rotatedCornersItem = shape.rotatedCorners(),
            rotatedCornersTarget = target.shape.rotatedCorners();

        itemFrom = rotatedCornersItem.bottomLeft.add(paddingVector.rotate(90 + shape.rotation));
        itemTo = rotatedCornersItem.bottomRight.add(paddingVector.rotate(90 + shape.rotation));

        if (target.type == 'width') {
            from = rotatedCornersTarget.bottomLeft.add(paddingVector.rotate(90 + target.shape.rotation));
            to = rotatedCornersTarget.bottomRight.add(paddingVector.rotate(90 + target.shape.rotation));
        }
        else{
            from = rotatedCornersTarget.bottomLeft.add(paddingVector.rotate(180 + target.shape.rotation));
            to = rotatedCornersTarget.topLeft.add(paddingVector.rotate(180 + target.shape.rotation));
        }

        let sourceArrow = PainterService.doubleArrow(itemFrom, itemTo , arrowSize);
        PathStyle.magneticLine(sourceArrow);
        group.addChild(sourceArrow);

        let targetArrow = PainterService.doubleArrow(from, to , arrowSize);
        PathStyle.magneticLine(targetArrow);
        group.addChild(targetArrow);
    }

    if (local.magneticMatch.height) {
        let from, to, itemFrom, itemTo;

        let shape = store.getters.selectedShape,
                target = local.magneticMatch.height.target,
                rotatedCornersItem = shape.rotatedCorners(),
                rotatedCornersTarget = target.shape.rotatedCorners();

        itemFrom = rotatedCornersItem.bottomLeft.add(paddingVector.rotate(180 + shape.rotation));
        itemTo = rotatedCornersItem.topLeft.add(paddingVector.rotate(180 + shape.rotation));

        if (target.type == 'width') {
            from = rotatedCornersTarget.bottomLeft.add(paddingVector.rotate(90 + target.shape.rotation));
            to = rotatedCornersTarget.bottomRight.add(paddingVector.rotate(90 + target.shape.rotation));
        }
        else{
            from = rotatedCornersTarget.bottomLeft.add(paddingVector.rotate(180 + target.shape.rotation));
            to = rotatedCornersTarget.topLeft.add(paddingVector.rotate(180 + target.shape.rotation));
        }

        let sourceArrow = PainterService.doubleArrow(itemFrom, itemTo , arrowSize);
        PathStyle.magneticLine(sourceArrow);
        group.addChild(sourceArrow);

        let targetArrow = PainterService.doubleArrow(from, to , arrowSize);
        PathStyle.magneticLine(targetArrow);
        group.addChild(targetArrow);
    }
}

function drawCenter(point) {
    let cross = new Group(),
    length = 6 / store.getters.zoom;
    cross.addChild(new Path.Line(
            new Point(point.x + length, point.y + length),
            new Point(point.x - length, point.y - length)
        )
    );
    cross.addChild(new Path.Line(
        new Point(point.x - length, point.y + length),
        new Point(point.x + length, point.y - length)
        )
    );
    PathStyle.magneticTarget(cross);
    return cross;
}

function getHitShape(event) {
    let hitShapes = getHitShapes(event)
    return hitShapes.length > 0 ? hitShapes[0] : null;
}

function getHitShapes(event) {
    let hitResults = paper.project.hitTestAll(event.point, hitOptions());

    if (hitResults == null) {
        local.itemId = null;
        return;
    }

    return hitResults.filter(h => h.item.layer.name == 'selectable')
        .sort((a, b) => a.item.bounds.area - b.item.bounds.area)
        .map(h => store.getters.shapes.find(shape => shape.pathId == h.item.id))
        .filter(s => s != null && s.accepts('select'));
}

function detectSizeMatch(size) {

    if (local.magneticSizeTargets.length == 0) return;
    local.magneticMatch = {height: null, width: null};
    let sizeMatchDeltaWidth = getClosestMagneticSize(size.width),
        sizeMatchDeltaHeight = getClosestMagneticSize(size.height);

    sizeMatchDeltaWidth.sort(function(a, b){return a.distance-b.distance});
    sizeMatchDeltaHeight.sort(function(a, b){return a.distance-b.distance});

    let closestWidth =  sizeMatchDeltaWidth[0];
    let closestHeight =  sizeMatchDeltaHeight[0];

    let epsilon = 8 / store.getters.zoom;

    if (closestWidth.distance < epsilon) local.magneticMatch.width = closestWidth;
    if (closestHeight.distance < epsilon) local.magneticMatch.height = closestHeight;
}

function getClosestMagneticSize(dimension) {
    if (local.magneticSizeTargets.length == 0) return null;
    let matchingSize = [];
    local.magneticSizeTargets.forEach(element => {
        let target = element;
            matchingSize.push({
                type : target.type,
                delta: target.value - dimension,
                distance: Math.abs(target.value - dimension),
                target
            })
        }) ;
    if(matchingSize.length != 0)
        return matchingSize;

    return false;
}

function select(shapes) {
    PainterService.removePosition();

    shapes = shapes.filter(s => s != null);
    if (shapes.length < 1) {
        deselect();
        return;
    }
    store.dispatch('setSelectedShapes', shapes);
    local.displayPosition = true;
    initSelectionRectangle();
}

function deselect() {
    PainterService.removePosition();
    store.dispatch('setSelectedShapes', []);

    paper.project.getItems({selected: true})
        .forEach(item => item.selected = false);

    let selectionRectangle = local.selectionRectangle;
    if (selectionRectangle) {
        selectionRectangle.remove();
    }

    if (local.customHandle) {
        local.customHandle.remove();
        local.customHandle = null;
    }
}

function onActivate() {
    initSelectionRectangle();
}


async function onKeyDown(event) {
    if (event.key === 'delete' && !store.getters.textFocus || event.key === 'backspace' && !store.getters.textFocus) {
        if (store.getters.selectedShapeIds.length > 1) {
            let shapesToRemove = [];
            let shapesToRemoveIds = [];
            local.selectedShapes.forEach(shape => {
                shapesToRemoveIds.push(shape.id);
                shapesToRemove.push(shape);
                let intersects = store.getters.shapes.filter(shapes => shape.bounds.intersects(shapes.bounds) &&  shape.id != shapes.id && shapes.isCut && !shape.isCut);
                intersects.forEach(intersect => {
                    shapesToRemoveIds.push(intersect.id);
                    shapesToRemove.push(intersect);
                })
            });
            shapesToRemove.forEach(shape =>{
                shape.removeItem();
            });
            store.dispatch('removeShapeIds',shapesToRemoveIds);
            deselect();
            return;
        }

        if(!store.getters.selectedShape.type.startsWith("cut")){
            if(await PathUtils.gotInternalCut(store.getters.selectedShape)) {
                var el = document.querySelector('#remove svg');
                var newone = el.cloneNode(true);
                el.parentNode.replaceChild(newone, el);
                
                return false;
            } 
        }
        if (store.getters.selectedShapeIds.length > 0) {
            local.selectedShapes.forEach(shape => {
                shape.removeItem();
            });
            store.dispatch('removeShapeIds', store.getters.selectedShapeIds);
            deselect();
        }
    }
    if(event.key === "space"){
        local.spaceKey = true;
    }

    if (event.key == "up" && !store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce(store.dispatch('moveShape', new Point(0, -1)));
    } else if (event.key == "up" && store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce();
    }

    if (event.key == "down" && !store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce(store.dispatch('moveShape', new Point(0, 1)));
    } else if (event.key == "down" && store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce();
    }


    if (event.key == "left" && !store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce(store.dispatch('moveShape', new Point(-1, 0)));
    }
    if (event.key == "right" && !store.getters.textFocus) {
        store.dispatch('preventRepaint');
        debounce(store.dispatch('moveShape', new Point(1, 0)));
    }

    if(event.key == "c" && (event.modifiers.control || event.modifiers.command)){
        if(local.selectedShapes.length > 0){
            let copy = [];
            local.selectedShapes.forEach(function(e){
                copy.push(e.clone());
            });
            local.copy = copy;
        }
    }

    if(event.key == "v" && (event.modifiers.control || event.modifiers.command)){
        if(local.copy.length > 0 && local.canPaste){
            local.canPaste = false;
            let delta ={
                x:local.mousePosition.x - (local.copy[0].hasOwnProperty('pointX') ? local.copy[0].pointX : local.copy[0].centerX),
                y:local.mousePosition.y - (local.copy[0].hasOwnProperty('pointY') ? local.copy[0].pointY : local.copy[0].centerY),
            };
            local.copy.forEach(function(e){
                e.move(delta);
            });
            await store.dispatch('addShapes', local.copy);
            local.canPaste = true;
        }
    }

}

function debounce(action) {
    clearTimeout(local.debounce);
    local.debounce = setTimeout(() => {
        if (action) {
            action;
        }
        store.dispatch('allowRepaint');
    }, 500);
}

async function onKeyUp(event) {
    if(event.key === "space"){
        local.spaceKey = false;
    }
}
export const SelecterTool = new Tool({
    name: 'selecter',
    onMouseDown,
    onMouseDrag,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    onActivate,
    initSelectionRectangle,
    onKeyUp,
    deselect,
    select
});
